import { useEffect, useState, useMemo } from "react";
import DateInput from '../../components/DateInput';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../config";
import { showSuccess } from "../../utils/dialog";
import { SignaturePanel } from "../../shared/SignaturePanel";
import "../../Styles/AccessibilityStndCustmer.css";
import talentLogo from "../../assets/talentlogo2.png";

const WHMISSafety = () => {
  const navigate = useNavigate();
  const itemId = "whmis-safety";
  const fieldOrder = useMemo(() => ["date", "employeeName", "employeeSignature"], []);
  const declarationStatements = useMemo(
    () => [
      "I understand that it is my responsibility to implement and adhere to the health and safety practices and WHMIS guidelines outlined in the material.",
      "I acknowledge that failure to follow these guidelines may result in consequences as per the company's policies",
      "I am aware that I can consult my supervisor or the Health and Safety Department if I have any questions or need further clarification regarding the material.",
    ],
    []
  );
  const requiredDeclarationCount = declarationStatements.length;
  

  // initialize formData including 'availability'
  const [formData, setFormData] = useState(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    const initial = saved ? JSON.parse(saved) : {};
    return {
      availability: Array.isArray(initial.availability) ? initial.availability : [],
      ...fieldOrder.reduce((acc, f) => {
        acc[f] = initial[f] ?? "";
        return acc;
      }, {}),
    };
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [err, setErr] = useState("");

  // load saved data
  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    if (saved) {
      const raw = JSON.parse(saved);
      setFormData({
        availability: Array.isArray(raw.availability) ? raw.availability : [],
        ...fieldOrder.reduce((acc, f) => {
          acc[f] = raw[f] ?? "";
          return acc;
        }, {}),
      });
    }
  }, [fieldOrder]);

  // Auto-save draft. Never set "complete" here (only on Submit). If already complete, preserve it when user reopens and goes back.
  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const existingStatus = accountId ? localStorage.getItem(`status_${accountId}_${itemId}`) : null;
    const existing = existingStatus || localStorage.getItem(`status_${itemId}`);
    const values = fieldOrder.map((f) => (formData[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const statusToPersist = allEmpty ? "incomplete" : (existing === "complete" ? "complete" : "in-progress");
    localStorage.setItem(`status_${accountId}_${itemId}`, statusToPersist);
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));
    if (accountId && statusToPersist !== "incomplete") {
      axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, {
        status: statusToPersist,
        formData: {}
      }).catch(err => console.error("Failed to sync status:", err));
    }
  }, [formData, fieldOrder]);

  // const isDateToday = (val) => {
  //   if (!val) return false;
  //   const input = new Date(val);
  //   const now = new Date();
  //   return (
  //     input.getFullYear() === now.getFullYear() &&
  //     input.getMonth() === now.getMonth() &&
  //     input.getDate() === now.getDate()
  //   );
  // };

  const isDateToday = (val) => {
    if (!val) return false;
    
    // Get current date in User's Local Time
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(now.getDate()).padStart(2, '0');

    // Create a standardized string "YYYY-MM-DD"
    const todayString = `${year}-${month}-${day}`;

    // Compare the strings directly
    return val === todayString;
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setErr("");
    setFieldErrors({});

    const errors = {};
    if (!formData.date) errors.date = "This field is required";
    else if (!isDateToday(formData.date)) errors.date = "Date must be today";
    if (!String(formData.employeeName || "").trim()) errors.employeeName = "This field is required";
    if (!formData.employeeSignature) errors.employeeSignature = "Signature is required";
    if ((formData.availability || []).length < requiredDeclarationCount) {
      errors.availability = `Please check all ${requiredDeclarationCount} declaration boxes`;
    }

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setErr("Please fill the required fields!");
      return;
    }

    const accountId = localStorage.getItem("accountId");
    if (!accountId) {
      return navigate("/register");
    }

    // Persist locally for fast reload
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));

    // Derive status from filled fields
    const values = fieldOrder.map((f) => (formData[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const allFilled = values.every((v) => v.length !== 0);
    const status = allEmpty ? "incomplete" : allFilled ? "complete" : "in-progress";
    const progress = status === "complete" ? 100 : status === "in-progress" ? 50 : 0;

    try {
      await axios.post(`${BASE_URL}/api/registration/${accountId}/items/${itemId}/complete`, {
        status,
        progress,
        formData,
      });
    } catch (saveErr) {
      console.error("Failed to save WHMIS Safety form to server:", saveErr);
      // keep existing UX: still return to checklist
    }
    if (status === "complete") {
      showSuccess("Form completed successfully!");
    }
    navigate("/register");
  };

  const handleBack = () => {
    navigate("/register");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      if (String(value || "").trim()) delete next[field];
      return next;
    });
  };

  // ✅ Added handler for checkboxes
  const handleCheckboxChange = (sentence) => {
    setFormData((prev) => {
      const availability = prev.availability.includes(sentence)
        ? prev.availability.filter((s) => s !== sentence) // remove if already selected
        : [...prev.availability, sentence]; // add if not selected

      setFieldErrors((prevErrors) => {
        if (!prevErrors.availability) return prevErrors;
        const nextErrors = { ...prevErrors };
        if (availability.length >= requiredDeclarationCount) {
          delete nextErrors.availability;
        }
        return nextErrors;
      });

      return { ...prev, availability };
    });
  };

  return (
    <div className="accessibility-form-page">
      <div className="form-container">
        <form className="form-card" onSubmit={handleSubmit}>
          <header className="form-card-header" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <img src={talentLogo} alt="Talent Employment Inc." style={{ height: 64, marginBottom: 10 }} />
              <h2 className="form-title" style={{ textAlign: "center" }}>
                WHMIS, and Health & Safety Acknowledgement
              </h2>
          </header>

          <section className="form-card-content">
            {err && (<div className="error-text" style={{ color: "#dc2626", marginBottom: 12 }}>{err}</div>)}
            <p className="form-intro">
              I hereby confirm that I have received, carefully read, and understood
              the Health and Safety reading material, as well as the Workplace
              Hazardous Materials Information System (WHMIS) information.
              Furthermore, I confirm I have read and understood the Workplace
              policies and Roles and responsibilities provided by Talent. I
              understand the importance of adhering to these guidelines to
              maintain a safe working environment for myself and my colleagues.
            </p>
            <p className="form-intro paragraph-space">
              I confirm that I have completed the Health and Safety Quiz and the
              WHMIS Quiz related to the reading material.
            </p>
            <p className="form-intro paragraph-space">Declaration:</p>

            <div className="long-sentences">
              {declarationStatements.map((sentence, index) => (
                <label key={index} className="block mb-2">
                  <input
                    type="checkbox"
                    checked={formData.availability.includes(sentence)}
                    onChange={() => handleCheckboxChange(sentence)}
                  />
                  {" "}
                  {sentence}
                </label>
              ))}
              {fieldErrors.availability && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.availability}</div>}
            </div>
          </section>

          <div className="grid-2">
            <div className="form-field">
              <label htmlFor="date">Date*</label>
              <DateInput
                id="date"
                value={formData.date}
                onChange={(v) => handleInputChange("date", v)}
                required
              />
              {fieldErrors.date && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.date}</div>}
            </div>

            <div className="form-field">
              <label htmlFor="employeeName">Employee's Full Name*</label>
              <input
                id="employeeName"
                type="text"
                value={formData.employeeName}
                onChange={(e) => handleInputChange("employeeName", e.target.value)}
                required
              />
              {fieldErrors.employeeName && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.employeeName}</div>}
            </div>
          </div>

          <div className="signature-panel-wrapper">
            <SignaturePanel
              onSignatureChange={(dataURL) =>
                handleInputChange("employeeSignature", dataURL)
              }
              initialSignature={formData.employeeSignature}
              required
            />
            {fieldErrors.employeeSignature && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.employeeSignature}</div>}
          </div>

          <div className="form-buttons">
            <button type="button" onClick={handleBack}>Back</button>
            <button type="submit" onClick={handleSubmit}>Submit</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WHMISSafety;
